<template lang="pug">
.product-price-stamp
  .flex
    .variant(v-if="variant && showVariantPrice")
      template(v-if="isHeadBuyer")
        .unit-price
          span.current {{ rangeUnitPrices | priceRange }}
          span.per per unit

        .case-price
          span.current {{ rangePrices | priceRange }}
          span.per per case
      template(v-else)
        .unit-price
          span.current(:class="{promoted: !!promotion}")
            template(v-if="applicablePromotion") {{ variant.case_price_cents | promotion-price(promotion, variant.case_units) }}
            template(v-else) {{ variant.case_price_cents / variant.case_units | cents }}
          span.old(v-if="promotions.length && applicablePromotion") {{ variant.case_price_cents / variant.case_units | cents }}
          price-change-stamp(:variant="variant")
          span.per per unit
        .case-price
          span.current(:class="{promoted: !!promotion}")
            template(v-if="applicablePromotion") {{ variant.case_price_cents | promotion-price(promotion) }}
            template(v-else) {{ variant.case_price_cents | cents }}
          span.old(v-if="promotions.length && applicablePromotion") {{ variant.case_price_cents | cents }}
          span.per per case

    .promotion(v-if="variant && promotion")
      .type.flex.aic
        template {{ promotion.promotion_type | promotionType }} Promotion
        help-tag(
          v-if="promotion.promotion_type === PROMOTION_TYPES.TPR || promotion.promotion_type === SHORT_PROMOTION_TYPES.TPR"
          :tooltip="promotion.promotion_type | promotionTypeHelpText")
      .applied(v-if="promotion.promo_action_type !== PROMOTION_ACTIONS_TYPES.STACKED")
        span.discounted(v-if="promotion.discount_type === 'Fix rate' && promotion.promotion_actions[0]")
          strong.value -{{ promotion.promotion_actions[0].discount_value | cents }}
          | /case
        span.discounted(v-else-if="promotion.discount_type === 'Percentage' && promotion.promotion_actions[0]") {{ promotion.promotion_actions[0].discount_value | percentage }} off
        span.divider
        span.new-price Only&nbsp;
          strong.value {{ variant.case_price_cents | promotion-price(promotion) }}
          | /case

      .case-minimum(v-if="promotion.minimum_num_case > 1")
        small Applicable with a minimum purchase of&nbsp;
          strong.value {{ promotion.minimum_num_case | number }}
          | &nbsp;cases

      .expiry-date(v-if="promotion.promotion_type === SHORT_PROMOTION_TYPES.SHORT_DATE")
        small This is a short-dated promotion. SKU expiry date:&nbsp;
          strong.value {{ promotion.sku_expiry_date | date }}

      .case-limit(v-if="promotion.case_limit")
        template(v-if="promotion.promotion_type === SHORT_PROMOTION_TYPES.BUY_IN")
          small Limited to first&nbsp;
            strong.value {{ promotion.case_limit | number }}
            | &nbsp;case{{ promotion.case_limit > 1 ? 's' : '' }} of the first order

        template(v-else)
          small Limited to first&nbsp;
            strong.value {{ promotion.case_limit | number }}
            | &nbsp;case{{ promotion.case_limit > 1 ? 's' : '' }} only

      CaseStackPromotions(:promotion="promotion")

</template>

<script>
import { mapGetters } from 'vuex'
import PriceChangeStamp from '~/components/stamps/PriceChangeStamp'
import CaseStackPromotions from '~/components/promotions/CaseStackPromotions'
import { PROMOTION_TYPES, SHORT_PROMOTION_TYPES, PROMOTION_ACTIONS_TYPES } from '~/settings'

export default {
  components: {
    PriceChangeStamp,
    CaseStackPromotions
  },

  props: {
    product: {
      type: Object,
      required: true
    },

    variant: {
      type: Object,
      default: null
    },

    quantity: {
      type: Number,
      default: 1
    }
  },

  data () {
    return {
      PROMOTION_TYPES,
      SHORT_PROMOTION_TYPES,
      PROMOTION_ACTIONS_TYPES
    }
  },

  computed: {
    ...mapGetters({
      isHeadBuyer: 'authentication/isHeadBuyer'
    }),

    rangePrices () {
      return [...new Set(this.variant?.variants_regions_info?.variants_regions_range_case_prices)]
    },

    rangeUnitPrices () {
      return this.rangePrices.map(e => e / this.variant.case_units)
    },

    promotions () {
      if (!this.variant) return []
      return this.variant.promotions || []
    },

    promotion () {
      return this.promotions.length ? this.promotions[0] || null : null
    },

    applicablePromotion () {
      if (!this.promotion) return false
      return this.totalQuantity >= this.promotion.minimum_num_case
    },

    totalQuantity () {
      return this.quantity + this.$store.getters['cart/addedQuantityBySku']({
        variant: this.variant
      })
    },

    showVariantPrice () {
      if (!this.isHeadBuyer) return true

      return this.rangePrices.length
    }
  }
}
</script>

<style lang="scss" scoped>
.product-price-stamp {
  .flex {
    flex-direction: column;
    @include media(sm) {
      flex-direction: row;
      .product,
      .variant {
        flex: inherit;
        border-radius: $--radius 0 0 $--radius;
        border-bottom: $--border-light;
        border-right: none;
        &:first-child:last-child {
          flex: 1;
          border-radius: $--radius;
          border: $--border-light;
        }
      }
      .promotion {
        padding: 12px 15px;
        border-radius: 0 $--radius $--radius 0;
      }
    }
  }
  .variant,
  .product {
    border: $--border-light;
    border-bottom: none;
    border-radius: $--radius $--radius 0 0;
    padding: 12px;
    white-space: nowrap;
    background: white;
    &:first-child:last-child {
      border-bottom: $--border-light;
      border-radius: $--radius;
    }
    .unit-price,
    .case-price {
      > *:not(:last-child) {
        margin-right: .3em;
      }
    }
    .unit-price span.current {
      color: $--color-primary;
    }
    span.current {
      font-size: 20px;
      font-weight: $--font-weight-bold;
    }
    span.old {
      font-size: 13px;
      text-decoration: line-through;
      color: darken($--color-text-secondary, 5);
    }
    span.per {
      color: $--color-text-secondary;
      font-size: 12px;
    }
  }
  .promotion {
    flex: 1;
    background: #fff7df;
    font-size: 12px;
    padding: 1em;
    border-radius: 0 0 $--radius $--radius;
    border: 1px solid darken(#fff7df, 15);
    .type {
      font-weight: $--font-weight-bold;
      color: $--color-warning;
      text-transform: uppercase;
      margin-bottom: 5px;
    }
    .divider {
      &:after {
        content: '-';
        margin: 0 .5em;
      }
    }
    small {
      font-size: 12px;
    }
  }
}
</style>
